import { Breakpoint, Typography } from '@altibox/design-system-component-lib';
import React, { FunctionComponent } from 'react';

import { BulletList } from 'src/components/bullet-points';
import { SummaryInformationCard } from 'src/components/summary-information-card';
import { i18n } from 'src/i18n';
import { selectSelectedProductOrThrow } from 'src/state/order/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { formatCurrency, formatCurrencyWithMonthlySuffix } from 'src/utils/currency-utils';
import { I18nUtils } from 'src/utils/i18n-utils';

import {
  StyledBulletListElement,
  StyledImage,
  StyledMonthlyPrice,
  StyledProductContainer,
  StyledProductInformation,
  StyledProductTitleAndPrice,
} from './product-information.styled';

const ProductInformation: FunctionComponent = () => {
  const product = useAppSelector(selectSelectedProductOrThrow);
  const { name, mainImage, pricing, includedItems } = product;
  const totalPrice =
    pricing.numberOfMonthlyInstallments && pricing.totalPriceForInstallmentPeriod
      ? {
          label: I18nUtils.message(i18n.page.summary.productInformation.totalPriceWithMonths, {
            numberOfMonthlyInstallments: pricing.numberOfMonthlyInstallments,
          }),
          value: formatCurrency(pricing.totalPriceForInstallmentPeriod),
        }
      : {
          label: i18n.page.summary.productInformation.totalPriceWithoutMonths,
          value: formatCurrency(pricing.firstYearPrice),
        };

  return (
    <SummaryInformationCard
      title={i18n.page.summary.productInformation.title}
      content={
        <StyledProductContainer>
          <StyledImage src={mainImage.url} alt={mainImage.altText} />
          <StyledProductInformation>
            <StyledProductTitleAndPrice>
              <Typography variant="paragraph2" component="p">
                {name}
              </Typography>
              <StyledMonthlyPrice variant="paragraph2" component="p" maxBreakpoint={Breakpoint.TABLET}>
                {formatCurrencyWithMonthlySuffix(pricing.monthlyPrice)}
              </StyledMonthlyPrice>
            </StyledProductTitleAndPrice>
            {includedItems && (
              <BulletList>
                {includedItems.map((item, index) => (
                  <StyledBulletListElement variant="paragraph3" key={index}>
                    {item}
                  </StyledBulletListElement>
                ))}
              </BulletList>
            )}
          </StyledProductInformation>
        </StyledProductContainer>
      }
      summaryItems={[
        {
          label: i18n.page.summary.productInformation.installationPrice,
          value: formatCurrency(pricing.installationPrice),
        },
        {
          label: i18n.page.summary.productInformation.monthlyPrice,
          value: formatCurrency(pricing.monthlyPrice),
        },
        totalPrice,
      ]}
    />
  );
};

export { ProductInformation };
